import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ruLocale from "@fullcalendar/core/locales/ru";
import frLocale from "@fullcalendar/core/locales/fr";
import trLocale from "@fullcalendar/core/locales/tr";
import { Button } from "@/components/ui/button";
import { StaffAvatar, StaffLegend } from "@/components/StaffAvatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/i18n/translations";
import { InboxPanel } from "@/components/InboxPanel";
import { PanelErrorBoundary } from "@/components/PanelErrorBoundary";
import { setBadge, pushSupported, isPushEnabled, enablePush } from "@/lib/push";
import { ProfileDialog } from "@/components/ProfileDialog";
import ClientHistoryBlock from "@/components/ClientHistoryBlock";
import RepeatRuleEditor, { defaultRepeat } from "@/components/RepeatRuleEditor";
import {
  ChevronLeft, ChevronRight, Plus, LogOut, Trash2, Menu, X, PhoneCall,
  Inbox, Users, UserCog, MessageSquare, Globe, CreditCard, Check, XCircle, MessageCircle, UserCircle, Settings, Megaphone, LifeBuoy, BarChart3 } from "lucide-react";
import Dashboard from "@/pages/Dashboard";
import AnnouncementBar from "@/components/AnnouncementBar";

const SLOT_PX = 16; // px per 15 min
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const t2m = (s) => parseInt(s.slice(0, 2)) * 60 + parseInt(s.slice(3, 5));
const m2t = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const SRC_ICON = { widget: "💬", whatsapp: "🟢", instagram: "📸", manual: "✋", phone: "📞", minisite: "🌐", altegio: "📒" };
const LOCALES = { ru: "ru-RU", en: "en-US", fr: "fr-FR", tr: "tr-TR" };
const FC_LOCALES = { ru: ruLocale, fr: frLocale, tr: trLocale };
const SETTINGS_ITEMS = [
  { tab: "settings", key: "tabs.settings", Icon: Settings },
  { tab: "bookings", key: "tabs.bookings", Icon: Inbox },
  { tab: "analytics", key: "tabs.analytics", Icon: BarChart3 },
  { tab: "clients", key: "tabs.clients", Icon: Users },
  { tab: "team", key: "tabs.team", Icon: UserCog },
  { tab: "widget", key: "tabs.widget", Icon: MessageSquare },
  { tab: "marketing", key: "tabs.marketing", Icon: Megaphone },
  { tab: "minisite", key: "tabs.minisite", Icon: Globe },
  { tab: "subscription", key: "tabs.subscription", Icon: CreditCard },
  { tab: "support", key: "tabs.support", Icon: LifeBuoy },
];
const ECOM_HIDDEN_TABS = ["bookings", "analytics", "team", "marketing", "minisite"];
const WEEK_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
// Effective working window for a master on a given date (date_overrides beat the weekly schedule)
const repeatWeekOn = (st, dateIso) => {
  const rep = st?.schedule_repeat || {};
  const n = parseInt(rep.every_weeks) || 1;
  if (n <= 1 || !rep.anchor) return true;
  if (rep.until && dateIso > rep.until) return true;
  const mon = (iso) => { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); return d; };
  const weeks = Math.round((mon(dateIso) - mon(rep.anchor)) / (7 * 86400000));
  return ((weeks % n) + n) % n === 0;
};
// Price of a service for a master/branch: master level → branch price → base
const ovFor = (svc, st) => { const ov = st?.service_overrides || {}; const k = Object.keys(ov).find((x) => x.toLowerCase() === (svc?.name || "").toLowerCase()); return k ? ov[k] || {} : {}; };
const calcDuration = (svc, st) => ovFor(svc, st).duration_min || svc?.duration_min || 60;
const calcPrice = (svc, st, branchId) => {
  if (!svc) return "";
  if (ovFor(svc, st).price != null) return ovFor(svc, st).price;
  const lp = svc.level_prices || {};
  if (st?.level && lp[st.level] != null) return lp[st.level];
  const bp = svc.branch_prices || {};
  const bid = branchId || ((st?.branch_ids || []).length === 1 ? st.branch_ids[0] : null);
  if (bid && bp[bid] != null) return bp[bid];
  return svc.price ?? "";
};
// Same rule as the backend engine: a master is capped by their primary branch's schedule (salon closed = master closed)
const capByBranch = (win, st, dow, branches) => {
  if (!win.enabled || !branches?.length) return win;
  const bid = (st?.branch_ids || [])[0] || (branches.find((b) => b.is_default) || branches[0]).id;
  const bday = (branches.find((b) => b.id === bid)?.schedule || {})[dow];
  if (!bday) return win;
  if (!bday.enabled) return { enabled: false, branchClosed: true };
  const start = (win.start || "10:00") > (bday.start || "00:00") ? win.start || "10:00" : bday.start || "00:00";
  const end = (win.end || "19:00") < (bday.end || "23:59") ? win.end || "19:00" : bday.end || "23:59";
  return start < end ? { ...win, start, end } : { enabled: false, branchClosed: true };
};
const dayWin = (st, dateIso, branches) => {
  const ov = (st?.date_overrides || {})[dateIso];
  const dow = WEEK_KEYS[(new Date(dateIso + "T00:00:00").getDay() + 6) % 7];
  const sch = (st?.schedule || {})[dow] || {};
  if (ov) return ov.enabled ? capByBranch({ enabled: true, start: ov.start || sch.start, end: ov.end || sch.end, note: ov.note, override: true }, st, dow, branches)
    : { enabled: false, note: ov.note, override: true };
  if (!repeatWeekOn(st, dateIso)) return { enabled: false, offWeek: true };
  return capByBranch({ enabled: !!sch.enabled, start: sch.start, end: sch.end }, st, dow, branches);
};

const playDing = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    o.start();
    o.frequency.setValueAtTime(1320, ctx.currentTime + 0.12);
    o.stop(ctx.currentTime + 0.55);
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch (e) { /* audio blocked */ }
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === "object" && d.code) return t(`cal.conflict.${d.code}`, d);
    return (typeof d === "string" && d) || fallback;
  };
  const locale = LOCALES[lang] || "en-US";
  const [view, setView] = useState("day"); // day | week | month
  const [anchor, setAnchor] = useState(new Date());
  const [data, setData] = useState({ bookings: [], staff: [], services: [], branches: [], external_busy: [], role: "owner" });
  const [branchFilter, setBranchFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");
  const [createDlg, setCreateDlg] = useState(null);
  const [detailDlg, setDetailDlg] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [settingsTab, setSettingsTab] = useState("settings");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("settings");
    if (p) {
      setMainView("settings");
      setSettingsTab(p === "billing" ? "subscription" : p === "integrations" ? "widget" : p);
      if (p === "analytics" && data?.role === "admin") setSettingsTab("team");
    }
    // eslint-disable-next-line
  }, []);
  const [busy, setBusy] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [monthMode, setMonthMode] = useState(() => localStorage.getItem("cal_month_mode") || "auto");
  // Unassigned panel: × hides it completely; it re-appears automatically when a NEW unassigned booking arrives
  const [unassignedOpen, setUnassignedOpen] = useState(true);
  const dismissUnassigned = () => {
    localStorage.setItem("cal_unassigned_dismissed", JSON.stringify(unassigned.map((b) => b.id)));
    setUnassignedOpen(false);
  };
  useEffect(() => {
    if (unassignedOpen) return;
    let dismissed = [];
    try { dismissed = JSON.parse(localStorage.getItem("cal_unassigned_dismissed") || "[]"); } catch { dismissed = []; }
    if (unassigned.some((b) => !dismissed.includes(b.id))) setUnassignedOpen(true);
  }, [unassigned, unassignedOpen]);
  const [history, setHistory] = useState(null); // {items, total, expanded}
  const [attendanceMissing, setAttendanceMissing] = useState(false);
  const [aiStats, setAiStats] = useState(null);
  const [aiPeriod, setAiPeriod] = useState("today");
  const [mainView, setMainView] = useState(() => (localStorage.getItem("chatity_cal_off") === "1" ? "inbox" : "calendar")); // calendar | inbox | settings
  const [unreadTotal, setUnreadTotal] = useState(0);
  useEffect(() => { setBadge(unreadTotal); }, [unreadTotal]);
  const [profileOpen, setProfileOpen] = useState(false);
  const lastClientAtRef = useRef(null);
  const knownIdsRef = useRef(null);
  const calDisabledRef = useRef(false);
  const isMaster = data.role === "master";
  const isEcom = data.business_type === "ecommerce";
  const calendarOff = data.role !== "master" && (data.calendar_enabled === false || isEcom);
  const calLoaded = data.calendar_enabled !== undefined || isMaster;
  useEffect(() => { if (isEcom && ECOM_HIDDEN_TABS.includes(settingsTab)) setSettingsTab("settings"); }, [isEcom, settingsTab]);

  useEffect(() => {
    if (isMaster) return;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const f = async () => {
      try {
        const r = await axios.get(`${API}/inbox/conversations`, { params: { limit: 1 } });
        setUnreadTotal(r.data.unread_total || 0);
        const at = r.data.last_client_at || "";
        if (lastClientAtRef.current !== null && at && at > lastClientAtRef.current) {
          const who = r.data.last_client_name || t("inbox.client");
          const body = r.data.last_client_text || "";
          playDing();
          toast(`💬 ${t("inbox.newMessage")}: ${who}`, { description: body, duration: 8000 });
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try { new Notification(`💬 ${t("inbox.newMessage")}: ${who}`, { body, tag: "chatity-inbox" }); } catch (e) { /* ignore */ }
          }
        }
        if (at) lastClientAtRef.current = at;
        else if (lastClientAtRef.current === null) lastClientAtRef.current = "";
      } catch (e) { /* silent */ }
    };
    f();
    const id = setInterval(f, 15000);
    return () => clearInterval(id);
  }, [isMaster]);

  useEffect(() => {
    if (isMaster) return;
    axios.get(`${API}/calendar/ai-stats`, { params: { period: aiPeriod } })
      .then((r) => setAiStats(r.data)).catch(() => {});
  }, [aiPeriod, isMaster]);

  const range = useMemo(() => {
    if (view === "day") return [anchor, anchor];
    if (view === "week") { const s = addDays(anchor, -((anchor.getDay() + 6) % 7)); return [s, addDays(s, 6)]; }
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    return [addDays(s, -7), addDays(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0), 7)];
  }, [view, anchor]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/calendar/bookings`, {
        params: { date_from: fmtDate(range[0]), date_to: fmtDate(range[1]) },
      });
      setData(r.data);
      let un = [];
      if (r.data.role !== "master") {
        try { un = (await axios.get(`${API}/calendar/bookings/unassigned`)).data.bookings || []; } catch (e) { /* ignore */ }
        setUnassigned(un);
      }
      const all = [...(r.data.bookings || []), ...un];
      if (knownIdsRef.current) {
        const recent = (b) => b.created_at && Date.now() - new Date(b.created_at).getTime() < 180000;
        const fresh = all.filter((b) => !knownIdsRef.current.has(b.id) && b.status !== "cancelled" && recent(b));
        if (fresh.length && fresh.length <= 3) {
          fresh.forEach((b) => toast(`🗓 ${t("cal.newBooking")}: ${b.client_name || ""}`, {
            description: `${b.service || ""}${b.start_at ? " · " + b.start_at.replace("T", " ").slice(5, 16) : b.preferred_date ? " · " + b.preferred_date : ""}`, duration: 8000 }));
        }
      }
      knownIdsRef.current = new Set(all.map((b) => b.id));
      if ((r.data.calendar_enabled === false || r.data.business_type === "ecommerce") && r.data.role !== "master") {
        localStorage.setItem("chatity_cal_off", "1");
        if (!calDisabledRef.current) {
          calDisabledRef.current = true;
          setMainView((v) => (v === "calendar" ? "inbox" : v));
        }
      } else {
        localStorage.removeItem("chatity_cal_off");
        calDisabledRef.current = false;
      }
    } catch (e) {
      if (e?.response?.status === 403) navigate("/dashboard");
    }
  }, [range, navigate]);

  useEffect(() => { load(); }, [load]);

  // Live updates: poll every 20s while the tab is visible + refresh on focus/visibility + on push messages from the SW
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const id = setInterval(tick, 20000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    const onMsg = (e) => { if (e.data?.type === "push" || e.data?.type === "booking") load(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      navigator.serviceWorker?.removeEventListener?.("message", onMsg);
    };
  }, [load]);

  // exactly ONE branch is always selected (default branch first)
  useEffect(() => {
    if (!branchFilter && data.branches.length) {
      const def = data.branches.find((b) => b.is_default) || data.branches[0];
      setBranchFilter(def.id);
    }
  }, [data.branches, branchFilter]);

  const changeBranch = (id) => { setBranchFilter(id); setStaffFilter("all"); };

  // masters of the selected branch only (staff without branch binding shown everywhere)
  const branchStaff = useMemo(() => data.staff.filter((s) =>
    !branchFilter || !(s.branch_ids || []).length || s.branch_ids.includes(branchFilter)
  ), [data.staff, branchFilter]);

  const staffShown = useMemo(() => branchStaff.filter(
    (s) => staffFilter === "all" || s.id === staffFilter), [branchStaff, staffFilter]);

  // Day scale follows the salon/branch working hours (fallback: staff schedules, then 8–21)
  const [hourStart, hourEnd] = useMemo(() => {
    let min = 24, max = 0;
    const collect = (sched) => {
      Object.values(sched || {}).forEach((d) => {
        if (!d?.enabled || !d.start || !d.end) return;
        min = Math.min(min, Math.floor(t2m(d.start) / 60));
        max = Math.max(max, Math.ceil(t2m(d.end) / 60));
      });
    };
    const branch = data.branches.find((b) => b.id === branchFilter);
    if (branch?.schedule) collect(branch.schedule);
    if (min >= max) branchStaff.forEach((s) => collect(s.schedule));
    // expand to include existing bookings outside the window
    data.bookings.forEach((b) => {
      if (!b.start_at) return;
      min = Math.min(min, Math.floor(t2m(b.start_at.slice(11, 16)) / 60));
      if (b.end_at) max = Math.max(max, Math.ceil(t2m(b.end_at.slice(11, 16)) / 60));
    });
    if (min >= max) return [8, 21];
    return [Math.max(0, min), Math.min(24, max)];
  }, [data.branches, data.bookings, branchFilter, branchStaff]);

  // only bookings of the visible masters (unassigned bookings — only for the selected branch)
  const visibleBookings = useMemo(() => {
    const ids = new Set(staffShown.map((s) => s.id));
    return data.bookings.filter((b) => b.staff_id
      ? ids.has(b.staff_id)
      : staffFilter === "all" && (!b.branch_id || !branchFilter || b.branch_id === branchFilter));
  }, [data.bookings, staffShown, staffFilter, branchFilter]);

  const dayBookings = useMemo(() => visibleBookings.filter(
    (b) => b.start_at && b.start_at.slice(0, 10) === fmtDate(anchor)), [visibleBookings, anchor]);

  // External (Planity) busy blocks for the visible masters — read-only
  const visibleBusy = useMemo(() => {
    const ids = new Set(staffShown.map((s) => s.id));
    return (data.external_busy || []).filter((e) => ids.has(e.staff_id));
  }, [data.external_busy, staffShown]);

  const dayBusy = useMemo(() => visibleBusy.filter(
    (e) => e.start_at && e.start_at.slice(0, 10) === fmtDate(anchor)), [visibleBusy, anchor]);

  // Month "load mosaic": per day → per master {count, busy minutes, work minutes}
  const mosaicOn = view === "month" && staffFilter === "all" && (monthMode === "mosaic" || (monthMode === "auto" && staffShown.length >= 5));
  const loadByDay = useMemo(() => {
    if (!mosaicOn) return {};
    const map = {};
    const ensure = (d) => (map[d] ||= Object.fromEntries(staffShown.map((st) => [st.id, { count: 0, busy: 0 }])));
    const add = (d, sid, start, end, isBooking) => {
      if (!d || !sid) return;
      const row = ensure(d)[sid];
      if (!row) return;
      const a = t2m(start.slice(11, 16)), b = end ? t2m(end.slice(11, 16)) : a + 30;
      row.busy += Math.max(0, b - a);
      if (isBooking) row.count += 1;
    };
    visibleBookings.forEach((b) => b.start_at && add(b.start_at.slice(0, 10), b.staff_id, b.start_at, b.end_at, true));
    visibleBusy.forEach((e) => e.start_at && add(e.start_at.slice(0, 10), e.staff_id, e.start_at, e.end_at, false));
    return map;
  }, [mosaicOn, visibleBookings, visibleBusy, staffShown]);

  const renderMosaicCell = (arg) => {
    const d = fmtDate(arg.date);
    const rows = loadByDay[d] || {};
    let total = 0, busy = 0, work = 0;
    const segs = staffShown.map((st) => {
      const sch = dayWin(st, d, data.branches);
      const wmin = sch?.enabled && sch.start && sch.end ? Math.max(0, t2m(sch.end) - t2m(sch.start)) : 0;
      const r = rows[st.id] || { count: 0, busy: 0 };
      total += r.count; busy += Math.min(r.busy, wmin || r.busy); work += wmin;
      const load = wmin ? Math.min(1, r.busy / wmin) : (r.busy ? 1 : 0);
      return { st, wmin, load, count: r.count };
    });
    const pct = work ? Math.round((busy / work) * 100) : 0;
    return (
      <div className={`cal-mosaic ${arg.isOther ? "opacity-40" : ""}`} data-testid="cal-mosaic-cell">
        <div className={`cal-mosaic-num ${arg.isToday ? "today" : ""}`}>{arg.dayNumberText}</div>
        {!arg.isOther && (
          <>
            <div className="cal-mosaic-bar">
              {segs.map(({ st, wmin, load, count }) => (
                <span key={st.id} data-testid={`cal-mosaic-seg-${st.id}`} title={`${st.name} · ${count} · ${wmin ? Math.round(load * 100) + "%" : t("cal.dayOff")}`}
                  className={`cal-mosaic-seg ${!wmin ? "off" : ""}`}
                  style={wmin ? { "--c": st.color || "#0ea5e9", "--a": `${Math.round(10 + load * 90)}%` } : {}}
                  onClick={(e) => { e.stopPropagation(); setAnchor(arg.date); setStaffFilter(st.id); setView("day"); }} />
              ))}
            </div>
            {total > 0 && <div className="cal-mosaic-sum">{total} · {pct}%</div>}
          </>
        )}
      </div>
    );
  };

  const moveBooking = async (id, start_at, staff_id) => {
    try {
      await axios.put(`${API}/calendar/bookings/${id}/move`, { start_at, staff_id });
      toast.success(t("cal.moved"));
    } catch (e) {
      toast.error(errMsg(e, t("cal.conflict")));
    }
    load();
  };

  const remindByCall = async (id) => {
    try {
      const r = await axios.post(`${API}/phone/remind-now/${id}`);
      toast.success(t("cal.remindCallDone").replace("{lang}", r.data.lang || ""));
    } catch (e) { toast.error(e?.response?.data?.detail || (e?.response?.status ? `HTTP ${e.response.status}` : e.message)); }
  };

  const cancelBooking = async (id) => {
    try {
      await axios.put(`${API}/calendar/bookings/${id}/cancel`);
      toast.success(t("cal.cancelled"));
      setDetailDlg(null);
      load();
    } catch (e) { toast.error(errMsg(e, "Error")); }
  };

  const [suggest, setSuggest] = useState([]);
  useEffect(() => {
    const q = createDlg?.client_name?.trim();
    if (!q || q.length < 2 || createDlg?._picked) { setSuggest([]); return; }
    const id = setTimeout(() => {
      axios.get(`${API}/calendar/clients/search`, { params: { q } })
        .then((r) => setSuggest(r.data.items || [])).catch(() => setSuggest([]));
    }, 250);
    return () => clearTimeout(id);
  }, [createDlg?.client_name, createDlg?._picked]);

  const pickClient = (c) => {
    setCreateDlg((d) => ({ ...d, client_name: c.client_name, phone: c.phone || d.phone, email: c.email || "", _picked: true }));
    setSuggest([]);
  };

  const createBooking = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/calendar/bookings`, {
        client_name: createDlg.client_name, phone: createDlg.phone || null,
        service: createDlg.service, staff_id: createDlg.staff_id || null,
        branch_id: createDlg.branch_id || null,
        price: createDlg.price === "" || createDlg.price == null ? null : parseFloat(createDlg.price),
        duration_min: parseInt(createDlg.duration_min) || null,
        note: createDlg.note || null,
        start_at: `${createDlg.date}T${createDlg.all_day ? "00:00" : createDlg.time}`,
        all_day: !!createDlg.all_day,
        repeat: createDlg.repeat ? { ...createDlg.repeat, until: createDlg.repeat.until || null } : null,
      });
      const r = res.data;
      if (r?.all_day || r?.series_id) {
        const skipped = (r.skipped || []).length;
        toast.success(`${t("cal.seriesCreated")}: ${r.created}` + (skipped ? ` · ${t("cal.seriesSkipped")}: ${skipped}` : ""));
      } else toast.success(t("cal.created"));
      setCreateDlg(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, t("cal.conflict")));
    } finally { setBusy(false); }
  };

  const openCreate = (preset = {}) => {
    const staff_id = preset.staff_id || staffShown[0]?.id || "";
    const branch_id = branchFilter || data.branches.find((b) => b.is_default)?.id || data.branches[0]?.id || "";
    setCreateDlg({
      client_name: "", phone: "", email: "", note: "",
      service: (servicesForStaff(staff_id)[0] || data.services[0])?.name || "",
      duration_min: calcDuration(servicesForStaff(staff_id)[0] || data.services[0], data.staff.find((s) => s.id === staff_id)),
      staff_id, branch_id,
      price: calcPrice(servicesForStaff(staff_id)[0] || data.services[0], data.staff.find((s) => s.id === staff_id), branch_id), _priceTouched: false,
      date: preset.date || fmtDate(anchor), time: preset.time || "12:00",
    });
  };

  // Strict services mode (any master has a service list): the dropdown shows only what the chosen master does
  const strictServices = data.staff.some((s) => (s.services || []).length > 0);
  const servicesForStaff = (staffId) => {
    const st = data.staff.find((x) => x.id === staffId);
    const mine = new Set([...(st?.services || []), ...Object.keys(st?.service_overrides || {})].map((x) => x.toLowerCase().trim()));
    if (!strictServices || !st || !mine.size) return data.services;
    return data.services.filter((sv) => mine.has((sv.name || "").toLowerCase().trim()));
  };

  // Re-derive the auto price unless the admin typed their own
  const patchCreate = (patch) => setCreateDlg((d) => {
    const next = { ...d, ...patch };
    if ("staff_id" in patch) {
      const allowed = servicesForStaff(next.staff_id);
      if (allowed.length && !allowed.find((x) => x.name === next.service)) next.service = allowed[0].name;
    }
    if ("service" in patch || "staff_id" in patch) {
      next.duration_min = calcDuration(data.services.find((x) => x.name === next.service), data.staff.find((x) => x.id === next.staff_id));
    }
    if (!next._priceTouched) {
      next.price = calcPrice(data.services.find((s) => s.name === next.service), data.staff.find((s) => s.id === next.staff_id), next.branch_id);
    }
    return next;
  });

  const onCreateService = (name) => {
    const svc = data.services.find((s) => s.name === name);
    patchCreate({ service: name });
  };

  const openDetail = (b) => {
    const svc = data.services.find((s) => s.name === b.service);
    setDetailDlg({
      ...b,
      client_name: b.client_name || "", phone: b.phone || "", email: b.email || "",
      note: b.note || "", products_used: b.products_used || "",
      date: b.start_at?.slice(0, 10) || fmtDate(anchor),
      time: b.start_at?.slice(11, 16) || "12:00",
      duration_min: b.duration_min || svc?.duration_min || 60,
      price: b.price ?? svc?.price ?? "",
      staff_id: b.staff_id || "",
    });
    setHistory(null);
    setAttendanceMissing(false);
    loadHistory(b.id, 5, false);
  };

  const loadHistory = async (bookingId, limit, expanded) => {
    try {
      const r = await axios.get(`${API}/calendar/clients/history`, { params: { booking_id: bookingId, limit } });
      setHistory({ ...r.data, expanded });
    } catch (e) { /* history is optional */ }
  };

  const onDetailService = (name) => {
    const svc = data.services.find((s) => s.name === name);
    setDetailDlg((d) => ({
      ...d, service: name,
      duration_min: svc?.duration_min || d.duration_min,
      price: calcPrice(svc, data.staff.find((s) => s.id === d.staff_id), d.branch_id) || d.price,
    }));
  };

  const saveDetail = async () => {
    if (isMaster && !detailDlg.attendance) {
      setAttendanceMissing(true);
      toast.error(t("cal.attendanceRequired"));
      return;
    }
    setBusy(true);
    try {
      const payload = isMaster
        ? { note: detailDlg.note, products_used: detailDlg.products_used }
        : {
          client_name: detailDlg.client_name, phone: detailDlg.phone, email: detailDlg.email,
          note: detailDlg.note, products_used: detailDlg.products_used,
          service: detailDlg.service, staff_id: detailDlg.staff_id || null,
          start_at: `${detailDlg.date}T${detailDlg.time}`,
          duration_min: parseInt(detailDlg.duration_min) || 60,
          price: detailDlg.price === "" || detailDlg.price === null ? null : parseFloat(detailDlg.price),
        };
      await axios.put(`${API}/calendar/bookings/${detailDlg.id}`, payload);
      toast.success(t("cal.saved"));
      setDetailDlg(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, t("cal.conflict")));
    } finally { setBusy(false); }
  };

  const confirmBooking = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/calendar/bookings/${detailDlg.id}/confirm`);
      toast.success(r.data.sent_via ? t("cal.confirmedSent") : t("cal.confirmedOk"));
      setDetailDlg((d) => ({ ...d, admin_confirmed: true }));
      load();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    } finally { setBusy(false); }
  };

  const setAttendance = async (val) => {
    const next = detailDlg.attendance === val ? "" : val;
    try {
      await axios.put(`${API}/calendar/bookings/${detailDlg.id}`, { attendance: next });
      setDetailDlg((d) => ({ ...d, attendance: next || null }));
      if (next) setAttendanceMissing(false);
      load();
    } catch (e) { toast.error(errMsg(e, "Error")); }
  };

  const fcEvents = useMemo(() => [
    ...visibleBookings.filter((b) => b.start_at).map((b) => {
      const st = data.staff.find((s) => s.id === b.staff_id);
      return {
        id: b.id, title: `${b.admin_confirmed === false ? "⏳ " : b.client_confirmed_at ? "✅ " : ""}${b.client_name || ""}`,
        start: b.start_at, end: b.end_at || b.start_at,
        backgroundColor: st?.color || "#0ea5e9", borderColor: st?.color || "#0ea5e9",
        extendedProps: { ...b, _color: st?.color || "#0ea5e9", _staff: st?.name || "" },
      };
    }),
    ...visibleBusy.map((e, i) => {
      const st = data.staff.find((s) => s.id === e.staff_id);
      const allDay = (e.start_at || "").slice(11) === "00:00" && (e.end_at || "").slice(11) >= "23:59";
      const title = allDay ? "Planity · закрыто" : e.kind === "block" ? "Planity · блок" : "Planity";
      return {
        id: `busy-${i}`, title,
        start: e.start_at, end: e.end_at || e.start_at,
        backgroundColor: st?.color || "#9ca3af", borderColor: st?.color || "#9ca3af", editable: false, classNames: ["fc-busy"],
        extendedProps: { _busy: true, _color: st?.color || "#9ca3af", _staff: st?.name || "" },
      };
    }),
  ], [visibleBookings, visibleBusy, data.staff]);

  // Closed days / non-working hours as background events (week + month). One master → their schedule;
  // all masters → a day is closed only when every shown master is off.
  const offEvents = useMemo(() => {
    if (view === "day" || !staffShown.length) return [];
    const out = [];
    const hs = `${String(hourStart).padStart(2, "0")}:00`, he = `${String(hourEnd).padStart(2, "0")}:00`;
    for (let d = new Date(range[0]); d <= range[1]; d = addDays(d, 1)) {
      const iso = fmtDate(d);
      const wins = staffShown.map((st) => ({ st, w: dayWin(st, iso, data.branches) }));
      const allOff = wins.every((x) => !x.w.enabled);
      if (allOff) {
        const note = wins.length === 1 ? (wins[0].w.note || (wins[0].w.override ? t("cal.vacation") : t("cal.dayOff"))) : t("cal.dayOff");
        out.push({ id: `off-${iso}`, start: view === "month" ? iso : `${iso}T${hs}:00`, end: view === "month" ? iso : `${iso}T${he}:00`, allDay: view === "month", display: "background",
          backgroundColor: "#e5e7eb", classNames: ["fc-offday"], extendedProps: { _off: true, _label: `🏖 ${note}` } });
        continue;
      }
      if (wins.length === 1 && view === "week" && wins[0].w.enabled && wins[0].w.start && wins[0].w.end) {
        const { start, end } = wins[0].w;
        if (start > hs) out.push({ id: `offa-${iso}`, start: `${iso}T${hs}:00`, end: `${iso}T${start}:00`, display: "background", backgroundColor: "#e5e7eb", classNames: ["fc-offday"], extendedProps: { _off: true } });
        if (end < he) out.push({ id: `offb-${iso}`, start: `${iso}T${end}:00`, end: `${iso}T${he}:00`, display: "background", backgroundColor: "#e5e7eb", classNames: ["fc-offday"], extendedProps: { _off: true } });
      }
    }
    return out;
  }, [view, range, staffShown, hourStart, hourEnd, t]);

  const renderEvent = (arg) => {
    const p = arg.event.extendedProps;
    if (p._off) return p._label ? <div className="text-[10px] font-semibold text-gray-500 px-1 pt-1 truncate" data-testid="cal-offday-label">{p._label}</div> : null;
    const hhmm = arg.event.start ? `${String(arg.event.start.getHours()).padStart(2, "0")}:${String(arg.event.start.getMinutes()).padStart(2, "0")}` : "";
    if (arg.view.type === "dayGridMonth") {
      return (
        <div className="cal-chip" style={{ "--chip": p._color }} title={`${hhmm} · ${p._staff}${p._busy ? " · Planity" : ` · ${p.client_name || ""} · ${p.service || ""}`}`} data-testid="cal-month-chip">
          <span className="cal-chip-bar" />
          <span className="cal-chip-time">{hhmm}</span>
          <span className={`cal-chip-text ${p._busy ? "cal-chip-busy" : ""}`}>{p._busy ? "🔒" : (arg.event.title || "")}</span>
        </div>
      );
    }
    return (
      <div className="px-1 leading-tight overflow-hidden" title={`${p._staff} · ${arg.event.title}${p.service ? ` · ${p.service}` : ""}`}>
        <div className="text-[11px] font-bold">{hhmm} {p._busy ? "🔒 Planity" : arg.event.title}</div>
        {!p._busy && <div className="text-[10px] opacity-90 truncate">{p.service || ""} · {p._staff}</div>}
      </div>
    );
  };

  const navStep = view === "day" ? 1 : view === "week" ? 7 : 30;
  const title = view === "month"
    ? anchor.toLocaleDateString(locale, { month: "long", year: "numeric" })
    : view === "week"
      ? `${range[0].toLocaleDateString(locale, { day: "numeric", month: "short" })} — ${range[1].toLocaleDateString(locale, { day: "numeric", month: "short" })}`
      : anchor.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });

  const pickDay = (d) => { setAnchor(d); setView("day"); setMainView("calendar"); setSideOpen(false); };

  const sidebarContent = (
    <>
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <img src="/logo-header.png" alt="Chatity" className="h-7 w-auto" data-testid="cal-sidebar-logo-img" />
        <button className="lg:hidden p-1" onClick={() => setSideOpen(false)} data-testid="cal-sidebar-close"><X className="w-5 h-5" /></button>
      </div>
      {!calendarOff && (
        <div className="px-3">
          <MiniCal anchor={anchor} onPick={pickDay} locale={locale} />
        </div>
      )}
      {/* Virtual AI administrator block — right under the calendar */}
      {!isMaster && aiStats && (
        <div className="mx-3 mt-3 border border-gray-200 rounded-xl p-3 bg-white" data-testid="cal-ai-block">
          {aiStats.ai_active ? (
            <>
              <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${data.billing_block ? "bg-red-500" : "bg-green-500 animate-pulse"}`} />
                {t("cal.aiAdmin")}
                {data.billing_block
                  ? <span className="text-red-600 font-medium ml-auto" data-testid="cal-ai-status-paused">{t("cal.aiPausedBilling")}</span>
                  : <span className="text-green-600 font-medium ml-auto">{t("cal.aiActive")}</span>}
              </div>
              <div className="flex bg-gray-100 rounded-md p-0.5 text-[10px] font-medium mb-2">
                {["today", "month"].map((p) => (
                  <button key={p} data-testid={`cal-ai-period-${p}`} onClick={() => setAiPeriod(p)}
                    className={`flex-1 px-2 py-0.5 rounded ${aiPeriod === p ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>
                    {t(p === "today" ? "cal.statToday" : "cal.statMonth")}
                  </button>
                ))}
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <div className="flex justify-between" data-testid="cal-ai-dialogs"><span>💬 {t("cal.dialogs")}</span><b className="text-gray-900">{aiStats.dialogs}</b></div>
                {!isEcom && (<>
                <div className="flex justify-between" data-testid="cal-ai-bookings"><span>📅 {t("cal.bookingsStat")}</span><b className="text-gray-900">{aiStats.bookings}</b></div>
                <div className="flex justify-between" data-testid="cal-ai-reschedules"><span>🔁 {t("cal.reschedules")}</span><b className="text-gray-900">{aiStats.reschedules}</b></div>
                <div className="flex justify-between" data-testid="cal-ai-cancellations"><span>🚫 {t("cal.statCancels")}</span><b className="text-gray-900">{aiStats.cancellations ?? 0}</b></div>
                </>)}
                {aiStats.channels?.phone && (
                  <div className="flex justify-between" data-testid="cal-ai-calls"><span>📞 {t("cal.statCalls")}</span><b className="text-gray-900">{aiStats.calls ?? 0}{aiStats.phone_bookings ? <span className="text-gray-400 font-normal"> · {aiStats.phone_bookings} 📅</span> : null}</b></div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5 text-sm font-medium mb-1">
                <span className="w-2 h-2 rounded-full bg-gray-300" />
                {t("cal.aiAdmin")}
              </div>
              <p className="text-xs text-gray-500 mb-2">{t("cal.aiPromo")}</p>
              <Button data-testid="cal-ai-connect-btn" onClick={() => navigate("/dashboard?tab=subscription")}
                className="w-full h-8 bg-gray-900 text-white hover:bg-gray-800 font-medium text-xs">
                {t("cal.aiConnect")} →
              </Button>
            </>
          )}
        </div>
      )}
      {/* Masters of the selected branch with today's hours */}
      {branchStaff.length > 0 && !calendarOff && (
        <div className="px-3 mt-3" data-testid="cal-sidebar-masters">
          <div className="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{t("cal.masters")}</div>
          {branchStaff.map((s) => {
            const day = dayWin(s, fmtDate(anchor), data.branches);
            const active = staffFilter === s.id;
            return (
              <button key={s.id} data-testid={`cal-sidebar-master-${s.id}`}
                onClick={() => { setStaffFilter(active ? "all" : s.id); setMainView("calendar"); setSideOpen(false); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${active ? "bg-gray-900 text-white" : "hover:bg-gray-100 text-gray-700"}`}>
                <StaffAvatar s={s} size={20} active={active} />
                <span className="truncate font-medium">{s.name}</span>
                <span className={`ml-auto text-xs whitespace-nowrap ${active ? "text-gray-300" : "text-gray-400"}`} title={day.note || ""}>
                  {day.override && !day.enabled ? `🏖 ${day.note || t("cal.vacation")}` : day.enabled ? `${day.start}–${day.end}` : t("cal.dayOff")}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {!isMaster && (
        <nav className="mt-2 px-2 flex-1" data-testid="cal-sidebar-nav">
          <button data-testid="cal-menu-inbox"
            onClick={() => { setMainView("inbox"); setSideOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${mainView === "inbox" ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"}`}>
            <MessageCircle className="w-4 h-4 shrink-0" />{t("cal.messages")}
            {unreadTotal > 0 && (
              <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="cal-inbox-unread-badge">{unreadTotal}</span>
            )}
          </button>
          {(data.role !== "admin" || data.permissions?.team !== false) && (
            <button data-testid="cal-menu-settings"
              onClick={() => { setMainView("settings"); setSideOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${mainView === "settings" ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"}`}>
              <Settings className="w-4 h-4 shrink-0" />{t("tabs.settings")}
            </button>
          )}
        </nav>
      )}
      {!isMaster && (
        <PushButton t={t} />
      )}
      {isMaster && <div className="flex-1" />}
      <div className="p-2 border-t border-gray-200">
        <button data-testid="cal-profile-btn" onClick={() => { setProfileOpen(true); setSideOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors">
          <UserCircle className="w-4 h-4" />{t("profile.title")}
        </button>
        <button data-testid="cal-logout-btn" onClick={() => { localStorage.clear(); window.location.href = "/auth"; }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors">
          <LogOut className="w-4 h-4" />{t("dashboard.logout") !== "dashboard.logout" ? t("dashboard.logout") : "Logout"}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex" data-testid="calendar-page">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-white border-r border-gray-200 h-screen sticky top-0 overflow-y-auto" data-testid="cal-sidebar">
        {sidebarContent}
      </aside>
      {/* Mobile drawer */}
      {sideOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="cal-sidebar-mobile">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSideOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col shadow-2xl overflow-y-auto overscroll-contain" data-testid="cal-sidebar-mobile-panel">
            {sidebarContent}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <ProfileDialog open={profileOpen} onClose={(saved) => { setProfileOpen(false); if (saved) load(); }} t={t} />
        {data.billing_block && !isMaster && (
          <button type="button" data-testid="billing-block-banner"
            onClick={() => { setMainView("settings"); setSettingsTab("subscription"); }}
            className="w-full bg-red-600 text-white text-sm font-semibold px-4 py-2 text-left flex items-center gap-2 hover:bg-red-700 transition-colors">
            <span>⚠️</span>
            <span className="flex-1">{data.billing_block === "payment_failed" ? t("cal.billingPaymentFailed") : t("cal.billingUnpaid")}</span>
            <span className="underline whitespace-nowrap">{t("cal.billingFix")} →</span>
          </button>
        )}
        {/* Header */}
        {mainView === "inbox" ? (
          <header className="bg-white border-b border-gray-200 px-3 sm:px-5 py-2.5 flex items-center gap-3 sticky top-0 z-20" data-testid="inbox-header">
            <button data-testid="cal-burger-btn-inbox" onClick={() => setSideOpen(true)}
              className="lg:hidden p-1.5 border border-gray-200 rounded-md hover:bg-gray-100 text-gray-600"><Menu className="w-4 h-4" /></button>
            <MessageCircle className="w-4 h-4 text-gray-500" />
            <div className="font-semibold text-sm sm:text-base">{t("cal.messages")}</div>
            {!calendarOff && (
              <button data-testid="inbox-to-calendar-btn" onClick={() => setMainView("calendar")}
                className="ml-auto px-3 py-1.5 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-100">
                ← {t("tabs.calendar") !== "tabs.calendar" ? t("tabs.calendar") : "Calendar"}
              </button>
            )}
          </header>
        ) : mainView === "settings" ? (
          <header className="bg-white border-b border-gray-200 px-3 sm:px-5 py-2.5 flex items-center gap-3 sticky top-0 z-20" data-testid="settings-header">
            <button data-testid="cal-burger-btn-settings" onClick={() => setSideOpen(true)}
              className="lg:hidden p-1.5 border border-gray-200 rounded-md hover:bg-gray-100 text-gray-600"><Menu className="w-4 h-4" /></button>
            <Settings className="w-4 h-4 text-gray-500" />
            <div className="font-semibold text-sm sm:text-base">{t("tabs.settings")}</div>
            {!calendarOff && (
              <button data-testid="settings-to-calendar-btn" onClick={() => {
                setMainView("calendar");
                const url = new URL(window.location); url.searchParams.delete("settings"); window.history.replaceState({}, "", url);
              }}
                className="ml-auto px-3 py-1.5 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-100">
                ← {t("tabs.calendar") !== "tabs.calendar" ? t("tabs.calendar") : "Calendar"}
              </button>
            )}
          </header>
        ) : (
        <header className="bg-white border-b border-gray-200 px-3 sm:px-5 py-2.5 flex items-center gap-2 sm:gap-4 flex-wrap sticky top-0 z-20">
          <button data-testid="cal-burger-btn" onClick={() => setSideOpen(true)}
            className="lg:hidden p-1.5 border border-gray-200 rounded-md hover:bg-gray-100 text-gray-600"><Menu className="w-4 h-4" /></button>
          <div className="flex items-center gap-1">
            <button data-testid="cal-prev-btn" onClick={() => setAnchor(addDays(anchor, -navStep))} className="p-1.5 border border-gray-200 rounded-md hover:bg-gray-100 text-gray-600"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="cal-today-btn" onClick={() => setAnchor(new Date())} className="px-3 py-1.5 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-100">{t("cal.today")}</button>
            <button data-testid="cal-next-btn" onClick={() => setAnchor(addDays(anchor, navStep))} className="p-1.5 border border-gray-200 rounded-md hover:bg-gray-100 text-gray-600"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="font-semibold text-sm sm:text-base capitalize" data-testid="cal-title">{title}</div>
          <div className="flex bg-gray-100 rounded-lg p-0.5 text-sm font-medium">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                className={`px-3 py-1 rounded-md transition-colors ${view === v ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-900"}`}>
                {t(`cal.${v}`)}
              </button>
            ))}
          </div>
          {data.branches.length > 1 && (
            <select data-testid="cal-branch-filter" value={branchFilter} onChange={(e) => changeBranch(e.target.value)}
              className="border border-gray-200 rounded-md px-2 py-1.5 text-sm font-medium bg-white text-gray-700">
              {data.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <div className="ml-auto flex items-center gap-2">
            {!isMaster && (
              <Button data-testid="cal-add-booking-btn" onClick={() => openCreate()}
                className="bg-gray-900 text-white hover:bg-gray-800 font-medium h-9 px-4 sm:px-6 hidden sm:flex">
                <Plus className="w-5 h-5 mr-1" />{t("cal.addClient")}
              </Button>
            )}
          </div>
        </header>
        )}

        {/* Mobile FAB */}
        {!isMaster && mainView === "calendar" && !calendarOff && calLoaded && (
          <button data-testid="cal-fab-btn" onClick={() => openCreate()}
            className="sm:hidden fixed bottom-5 right-5 z-30 bg-gray-900 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-xl">
            <Plus className="w-7 h-7" />
          </button>
        )}

        <AnnouncementBar />
        <main className={`flex-1 p-2 sm:p-4 ${mainView === "settings" ? "overflow-visible" : "overflow-auto"}`}>
          {mainView === "inbox" && (
            <PanelErrorBoundary message={t("inbox.crashMsg") !== "inbox.crashMsg" ? t("inbox.crashMsg") : "Не удалось отобразить диалог"} reloadLabel={t("inbox.reload") !== "inbox.reload" ? t("inbox.reload") : "Обновить"}>
              <InboxPanel t={t} onUnread={setUnreadTotal} />
            </PanelErrorBoundary>
          )}
          {mainView === "settings" && (
            <div className="flex flex-col xl:flex-row gap-3 items-start" data-testid="cal-settings-layout">
              <aside className="w-full xl:w-56 shrink-0 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden xl:sticky xl:top-[68px] flex xl:flex-col overflow-x-auto xl:overflow-x-visible" data-testid="cal-settings-menu">
                {SETTINGS_ITEMS.filter(({ tab }) =>
                  (data.role !== "admin" || (tab === "team" && data.permissions?.team !== false))
                  && !(isEcom && ECOM_HIDDEN_TABS.includes(tab))
                ).map(({ tab, key, Icon }) => (
                  <button key={tab} data-testid={`cal-settings-${tab}`}
                    onClick={() => {
                      setSettingsTab(tab);
                      const url = new URL(window.location); url.searchParams.set("settings", tab); window.history.replaceState({}, "", url);
                    }}
                    className={`shrink-0 xl:w-full flex items-center gap-2 xl:gap-3 px-3.5 py-2.5 text-sm font-semibold text-left whitespace-nowrap transition-colors ${settingsTab === tab ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100"}`}>
                    <Icon className="w-4 h-4 shrink-0" />{t(key)}
                  </button>
                ))}
              </aside>
              <div className="flex-1 min-w-0 w-full" data-testid="cal-settings-content">
                <Dashboard embedded tab={settingsTab} setIsAuthenticated={() => {}} />
              </div>
            </div>
          )}
          {mainView === "calendar" && !calendarOff && calLoaded && (
            <div className="flex flex-col md:flex-row gap-3 items-start">
              {!isMaster && unassigned.length > 0 && unassignedOpen && (
                <aside className="w-full md:w-56 shrink-0 bg-white border border-amber-300 rounded-xl shadow-sm overflow-hidden" data-testid="cal-unassigned-col">
                  <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 font-semibold text-sm flex items-center gap-1.5 text-amber-900">
                    ⏳ {t("cal.unassigned")}
                    <span className="ml-auto bg-amber-500 text-white text-xs rounded-full px-2 py-0.5" data-testid="cal-unassigned-count">{unassigned.length}</span>
                    <button onClick={dismissUnassigned} data-testid="cal-unassigned-close" className="p-0.5 rounded hover:bg-amber-200 text-amber-800" title="×"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="md:max-h-[70vh] md:overflow-y-auto md:divide-y md:divide-gray-100 flex md:block overflow-x-auto">
                    {unassigned.map((b) => (
                      <button key={b.id} data-testid={`cal-unassigned-card-${b.id}`} onClick={() => openDetail(b)}
                        className="w-60 md:w-full shrink-0 text-left px-3 py-2.5 hover:bg-amber-50/70 transition-colors border-r border-gray-100 md:border-r-0">
                        <div className="font-medium text-sm truncate">{b.client_name || "—"}</div>
                        <div className="text-xs text-gray-600 truncate">{b.service}</div>
                        {b.preferred_date && <div className="text-xs text-gray-500 truncate">🗓 {b.preferred_date}</div>}
                        {b.needs_callback && <div className="text-[11px] font-semibold text-red-600 mt-0.5" data-testid={`cal-unassigned-callback-${b.id}`}>📞 {t("cal.needsCallback")}</div>}
                        {b.note && <div className="text-[11px] text-gray-500 italic line-clamp-2 mt-0.5">💬 {b.note}</div>}
                        <div className="text-[11px] mt-1 flex items-center justify-between gap-1">
                          <span className="text-gray-400 truncate">{SRC_ICON[b.source] || "💬"} {b.phone || ""}</span>
                          <span className="underline font-semibold text-amber-700 whitespace-nowrap">{t("cal.assignTime")}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </aside>
              )}
              <div className="flex-1 min-w-0 w-full">
                {branchStaff.length > 1 && (
                  <div className="mb-2 px-0.5 flex items-center gap-2 flex-wrap">
                    <StaffLegend staff={branchStaff} value={staffFilter} onChange={setStaffFilter} allLabel={t("cal.allMasters")} />
                    {view === "month" && staffFilter === "all" && (
                      <div className="ml-auto flex bg-gray-100 rounded-lg p-0.5 text-xs font-semibold" data-testid="cal-month-mode">
                        {[["chips", t("cal.modeChips")], ["mosaic", t("cal.modeLoad")]].map(([k, label]) => {
                          const on = k === "mosaic" ? mosaicOn : !mosaicOn;
                          return <button key={k} data-testid={`cal-month-mode-${k}`} onClick={() => { setMonthMode(k); localStorage.setItem("cal_month_mode", k); }}
                            className={`px-2.5 py-1 rounded-md transition-colors ${on ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-900"}`}>{label}</button>;
                        })}
                      </div>
                    )}
                  </div>
                )}
                {view === "day" && (
                  <DayGrid bookings={dayBookings} busy={dayBusy} staff={staffShown} date={fmtDate(anchor)} readOnly={isMaster} branches={data.branches}
                    hourStart={hourStart} hourEnd={hourEnd}
                    onMove={moveBooking} onCellClick={(staff_id, time) => !isMaster && openCreate({ staff_id, time, date: fmtDate(anchor) })}
                    onCardClick={openDetail} allStaff={data.staff} />
                )}
                {view !== "day" && (
                  <div className={`bg-white border border-gray-200 rounded-xl shadow-sm p-2 ${mosaicOn ? "fc-mosaic" : ""}`} data-testid="cal-fullcalendar">
                    <FullCalendar
                      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      initialView={view === "week" ? "timeGridWeek" : "dayGridMonth"}
                      initialDate={anchor}
                      key={`${view}-${fmtDate(anchor)}-${mosaicOn ? "m" : "c"}`}
                      headerToolbar={false}
                      locale={FC_LOCALES[lang]}
                      firstDay={1}
                      allDaySlot={false}
                      slotMinTime={`${String(hourStart).padStart(2, "0")}:00:00`}
                      slotMaxTime={`${String(hourEnd).padStart(2, "0")}:00:00`}
                      height="auto"
                      events={mosaicOn ? offEvents : [...fcEvents, ...offEvents]}
                      dayCellContent={mosaicOn ? renderMosaicCell : undefined}
                      eventContent={renderEvent}
                      dayMaxEvents={4}
                      moreLinkContent={(a) => `+${a.num}`}
                      eventOrder="start"
                      editable={!isMaster && view === "week"}
                      eventDrop={(info) => {
                        const s = info.event.start;
                        moveBooking(info.event.id, `${fmtDate(s)}T${String(s.getHours()).padStart(2, "0")}:${String(s.getMinutes()).padStart(2, "0")}`, info.event.extendedProps.staff_id);
                      }}
                      eventClick={(info) => { if (!info.event.extendedProps._busy) openDetail(info.event.extendedProps); }}
                      dateClick={(info) => { if (view === "month") { setAnchor(info.date); setView("day"); } }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Create dialog */}
      <Dialog open={!!createDlg} onOpenChange={(o) => !o && setCreateDlg(null)}>
        <DialogContent className="text-gray-900 max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("cal.newBooking")}</DialogTitle></DialogHeader>
          {createDlg && (
            <div className="space-y-3">
              <div className="relative"><Label>{t("cal.clientName")}</Label>
                <Input data-testid="cal-create-name" value={createDlg.client_name} autoComplete="off"
                  onChange={(e) => setCreateDlg({ ...createDlg, client_name: e.target.value, _picked: false })} className="h-9" />
                {suggest.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-testid="cal-create-suggest">
                    {suggest.map((c, i) => (
                      <button key={i} type="button" data-testid={`cal-create-suggest-${i}`}
                        onClick={() => pickClient(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex justify-between gap-2">
                        <span className="font-medium truncate">{c.client_name}</span>
                        <span className="text-gray-400 whitespace-nowrap">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div><Label>{t("cal.phone")}</Label>
                <Input data-testid="cal-create-phone" value={createDlg.phone} onChange={(e) => setCreateDlg({ ...createDlg, phone: e.target.value })} className="h-9" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label>{t("cal.service")}</Label>
                  <Select value={createDlg.service} onValueChange={onCreateService}>
                    <SelectTrigger data-testid="cal-create-service" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {servicesForStaff(createDlg.staff_id).map((s) => <SelectItem key={s.id} value={s.name}>{s.name} ({s.duration_min} {t("team.min")})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {strictServices && servicesForStaff(createDlg.staff_id).length < data.services.length && (
                    <p className="text-[11px] text-gray-500 mt-1" data-testid="cal-create-service-hint">{t("cal.onlyMasterServices")}</p>
                  )}</div>
                <div><Label>{t("cal.duration")}</Label>
                  <Input data-testid="cal-create-duration" type="number" min="5" step="5" value={createDlg.duration_min}
                    onChange={(e) => setCreateDlg({ ...createDlg, duration_min: e.target.value })} className="h-9" /></div>
              </div>
              <div><Label>{t("cal.master")}</Label>
                <Select value={createDlg.staff_id} onValueChange={(v) => patchCreate({ staff_id: v })}>
                  <SelectTrigger data-testid="cal-create-master" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{branchStaff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              {data.branches.length > 1 && (
                <div><Label>{t("team.branches")}</Label>
                  <Select value={createDlg.branch_id} onValueChange={(v) => patchCreate({ branch_id: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
              <div className="flex items-center gap-4 flex-wrap text-sm">
                <label className="flex items-center gap-2 cursor-pointer font-medium">
                  <input type="checkbox" data-testid="cal-create-allday" checked={!!createDlg.all_day}
                    onChange={(e) => setCreateDlg({ ...createDlg, all_day: e.target.checked })} className="w-4 h-4 accent-gray-900" />
                  {t("cal.allDay")}
                </label>
                <button type="button" data-testid="cal-create-repeat-toggle"
                  onClick={() => setCreateDlg({ ...createDlg, repeat: createDlg.repeat ? null : defaultRepeat(createDlg.date) })}
                  className={`underline underline-offset-2 ${createDlg.repeat ? "text-green-700 font-semibold" : "text-gray-600 hover:text-black"}`}>
                  🔁 {createDlg.repeat ? t("cal.repeatOn") : t("cal.repeat")}
                </button>
                {createDlg.all_day && <span className="text-xs text-gray-500" data-testid="cal-create-allday-hint">{t("cal.allDayHint")}</span>}
              </div>
              {createDlg.repeat && <RepeatRuleEditor value={createDlg.repeat} onChange={(v) => setCreateDlg({ ...createDlg, repeat: v })} />}
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("cal.date")}</Label>
                  <Input data-testid="cal-create-date" type="date" value={createDlg.date} onChange={(e) => setCreateDlg({ ...createDlg, date: e.target.value })} className="h-9" /></div>
                {!createDlg.all_day && <div><Label>{t("cal.time")}</Label>
                  <Input data-testid="cal-create-time" type="time" step="900" value={createDlg.time} onChange={(e) => setCreateDlg({ ...createDlg, time: e.target.value })} className="h-9" /></div>}
                {!createDlg.all_day && <div><Label>{t("cal.price")} (€)</Label>
                  <Input data-testid="cal-create-price" type="number" min="0" step="0.5" value={createDlg.price ?? ""}
                    onChange={(e) => setCreateDlg({ ...createDlg, price: e.target.value, _priceTouched: true })} className="h-9" />
                  {(() => {
                    const auto = calcPrice(data.services.find((s) => s.name === createDlg.service), data.staff.find((s) => s.id === createDlg.staff_id), createDlg.branch_id);
                    return createDlg._priceTouched && String(auto) !== String(createDlg.price) && auto !== ""
                      ? <button type="button" className="text-[11px] text-gray-500 underline mt-0.5" data-testid="cal-create-price-reset" onClick={() => setCreateDlg({ ...createDlg, price: auto, _priceTouched: false })}>{t("cal.priceAuto")}: {auto} €</button>
                      : null;
                  })()}
                </div>}
              </div>
              <div><Label>{t("cal.note")}</Label>
                <textarea data-testid="cal-create-note" value={createDlg.note} rows={2}
                  onChange={(e) => setCreateDlg({ ...createDlg, note: e.target.value })}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-gray-400" /></div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="cal-create-save" onClick={createBooking}
              disabled={busy || !createDlg?.client_name?.trim() || (!createDlg?.all_day && !createDlg?.service) || (createDlg?.all_day && !createDlg?.staff_id)}
              className="bg-gray-900 text-white hover:bg-gray-800 font-medium w-full h-10">{t("cal.book")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / edit dialog */}
      <Dialog open={!!detailDlg} onOpenChange={(o) => !o && setDetailDlg(null)}>
        <DialogContent className="text-gray-900 max-h-[88vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle>{t("cal.booking")}</DialogTitle>
              <Button data-testid="cal-detail-save" onClick={saveDetail} disabled={busy}
                className="bg-gray-900 text-white hover:bg-gray-800 font-medium h-9 px-6">{t("cal.save")}</Button>
            </div>
          </DialogHeader>
          {detailDlg && (
            <div className="space-y-3" data-testid="cal-detail">
              {detailDlg.client_confirmed_at && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-lg px-3 py-2 text-sm font-medium text-green-800" data-testid="cal-detail-client-confirmed">
                  <Check className="w-4 h-4" />{detailDlg.client_confirmed_via === "phone" ? t("cal.clientConfirmedPhone") : t("cal.clientConfirmed")} · {(detailDlg.client_confirmed_at || "").replace("T", " ").slice(0, 16)}
                </div>
              )}
              {!isMaster && detailDlg.admin_confirmed === false && (
                <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2" data-testid="cal-detail-pending">
                  <span className="text-sm font-medium text-amber-800">⏳ {t("cal.pendingConfirm")}</span>
                  <Button data-testid="cal-detail-confirm-btn" onClick={confirmBooking} disabled={busy}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium h-8 px-4 shrink-0">
                    <Check className="w-4 h-4 mr-1" />{t("cal.confirmBtn")}
                  </Button>
                </div>
              )}
              {isMaster ? (
                <div className="space-y-3" data-testid="cal-detail-master-view">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("cal.clientName")}</Label>
                      <div className="h-9 flex items-center px-3 rounded-md bg-gray-50 border border-gray-200 text-sm font-semibold" data-testid="cal-detail-name">{detailDlg.client_name || "—"}</div></div>
                    <div><Label>{t("cal.service")}</Label>
                      <div className="h-9 flex items-center px-3 rounded-md bg-gray-50 border border-gray-200 text-sm" data-testid="cal-detail-service">{detailDlg.service || "—"}</div></div>
                    <div><Label>{t("cal.date")}</Label>
                      <div className="h-9 flex items-center px-3 rounded-md bg-gray-50 border border-gray-200 text-sm" data-testid="cal-detail-date">{detailDlg.date}</div></div>
                    <div><Label>{t("cal.time")}</Label>
                      <div className="h-9 flex items-center px-3 rounded-md bg-gray-50 border border-gray-200 text-sm" data-testid="cal-detail-time">{detailDlg.time}</div></div>
                  </div>
                  <div className={`rounded-lg border-2 p-3 ${detailDlg.attendance === "came" && !detailDlg.products_used ? "border-amber-400 bg-amber-50" : "border-gray-900 bg-white"}`}>
                    <Label className="font-bold">{t("cal.whatDone")}</Label>
                    <p className="text-xs text-gray-500 mb-1.5">{t("cal.whatDoneHint")}</p>
                    <textarea data-testid="cal-detail-products" value={detailDlg.products_used} rows={2}
                      placeholder="Wella 7/1 + 6% 1:1, гель Luxio Blush, лак OPI 'Bubble Bath'…"
                      onChange={(e) => setDetailDlg({ ...detailDlg, products_used: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-y min-h-[56px] focus:outline-none focus:ring-1 focus:ring-gray-400" />
                    <Label className="mt-2 block">{t("cal.note")}</Label>
                    <textarea data-testid="cal-detail-note" value={detailDlg.note} rows={1}
                      onChange={(e) => setDetailDlg({ ...detailDlg, note: e.target.value })}
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm resize-y min-h-[40px] focus:outline-none focus:ring-1 focus:ring-gray-400" />
                    {(detailDlg.comments || []).map((c) => (
                      <div key={c.id} className="text-xs text-gray-600 pl-2 border-l-2 border-gray-200 mt-1" data-testid={`cal-detail-comment-${c.id}`}>
                        <span className="text-gray-400">{(c.at || "").slice(0, 10)} · {c.author}:</span> {c.text}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (<>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>{t("cal.clientName")}</Label>
                  <Input data-testid="cal-detail-name" disabled={isMaster} value={detailDlg.client_name}
                    onChange={(e) => setDetailDlg({ ...detailDlg, client_name: e.target.value })} className="h-9" /></div>
                <div><Label>{t("cal.phone")}</Label>
                  <Input data-testid="cal-detail-phone" disabled={isMaster} value={detailDlg.phone}
                    onChange={(e) => setDetailDlg({ ...detailDlg, phone: e.target.value })} className="h-9" /></div>
                <div><Label>{t("cal.email")}</Label>
                  <Input data-testid="cal-detail-email" disabled={isMaster} type="email" value={detailDlg.email}
                    onChange={(e) => setDetailDlg({ ...detailDlg, email: e.target.value })} className="h-9" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("cal.date")}</Label>
                  <Input data-testid="cal-detail-date" disabled={isMaster} type="date" value={detailDlg.date}
                    onChange={(e) => setDetailDlg({ ...detailDlg, date: e.target.value })} className="h-9" /></div>
                <div><Label>{t("cal.time")}</Label>
                  <Input data-testid="cal-detail-time" disabled={isMaster} type="time" step="900" value={detailDlg.time}
                    onChange={(e) => setDetailDlg({ ...detailDlg, time: e.target.value })} className="h-9" /></div>
                <div><Label>{t("cal.duration")}</Label>
                  <Input data-testid="cal-detail-duration" disabled={isMaster} type="number" min="5" step="5" value={detailDlg.duration_min}
                    onChange={(e) => setDetailDlg({ ...detailDlg, duration_min: e.target.value })} className="h-9" /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>{t("cal.service")}</Label>
                  {data.services.length && !isMaster ? (
                    <Select value={detailDlg.service || ""} onValueChange={onDetailService}>
                      <SelectTrigger data-testid="cal-detail-service" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {!data.services.find((s) => s.name === detailDlg.service) && detailDlg.service &&
                          <SelectItem value={detailDlg.service}>{detailDlg.service}</SelectItem>}
                        {servicesForStaff(detailDlg.staff_id).map((s) => <SelectItem key={s.id} value={s.name}>{s.name} ({s.duration_min} {t("team.min")})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid="cal-detail-service" disabled={isMaster} value={detailDlg.service || ""}
                      onChange={(e) => setDetailDlg({ ...detailDlg, service: e.target.value })} className="h-9" />
                  )}
                </div>
                <div><Label>{t("cal.master")}</Label>
                  <Select value={detailDlg.staff_id || ""} onValueChange={(v) => setDetailDlg({ ...detailDlg, staff_id: v })} disabled={isMaster}>
                    <SelectTrigger data-testid="cal-detail-master" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>{t("cal.price")} (€)</Label>
                  <Input data-testid="cal-detail-price" disabled={isMaster} type="number" min="0" step="0.5" value={detailDlg.price}
                    onChange={(e) => setDetailDlg({ ...detailDlg, price: e.target.value })} className="h-9" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("cal.products")}</Label>
                  <Input data-testid="cal-detail-products" value={detailDlg.products_used}
                    placeholder="Wella 7/1, гель Luxio…"
                    onChange={(e) => setDetailDlg({ ...detailDlg, products_used: e.target.value })} className="h-9" /></div>
                <div><Label>{t("cal.note")}</Label>
                  <textarea data-testid="cal-detail-note" value={detailDlg.note} rows={1}
                    onChange={(e) => setDetailDlg({ ...detailDlg, note: e.target.value })}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm resize-y min-h-[40px] focus:outline-none focus:ring-1 focus:ring-gray-400" />
                  {(detailDlg.comments || []).map((c) => (
                    <div key={c.id} className="text-xs text-gray-600 pl-2 border-l-2 border-gray-200 mt-1" data-testid={`cal-detail-comment-${c.id}`}>
                      <span className="text-gray-400">{(c.at || "").slice(0, 10)} · {c.author}:</span> {c.text}
                    </div>
                  ))}</div>
              </div>
              </>)}
              {/* Attendance — available to masters too */}
              {isMaster && !detailDlg.attendance && (
                <div className={`text-sm font-semibold rounded-md px-3 py-2 ${attendanceMissing ? "bg-red-50 text-red-700 border border-red-300" : "bg-amber-50 text-amber-800 border border-amber-300"}`} data-testid="cal-detail-attendance-required">
                  {t("cal.attendanceRequired")}
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button data-testid="cal-detail-came" onClick={() => setAttendance("came")}
                  className={`flex-1 sm:flex-none sm:px-8 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${detailDlg.attendance === "came" ? "bg-green-600 border-green-600 text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-green-50 hover:border-green-300"}`}>
                  <Check className="w-4 h-4" />{t("cal.came")}
                </button>
                <button data-testid="cal-detail-noshow" onClick={() => setAttendance("no_show")}
                  className={`flex-1 sm:flex-none sm:px-8 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${detailDlg.attendance === "no_show" ? "bg-red-600 border-red-600 text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-red-50 hover:border-red-300"}`}>
                  <XCircle className="w-4 h-4" />{t("cal.noShow")}
                </button>
                {!isMaster && <span className="ml-auto font-semibold text-base" data-testid="cal-detail-total">
                  {t("cal.total")}: {detailDlg.price === "" || detailDlg.price === null ? "—" : `${detailDlg.price} €`}
                </span>}
              </div>
              {/* Visit history — available to all roles */}
              <ClientHistoryBlock detail={detailDlg} history={history} setHistory={setHistory}
                onShowMore={() => loadHistory(detailDlg.id, 200, true)} />
              {/* Footer line: source + small delete icon (owner/admin only) */}
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-gray-400">{SRC_ICON[detailDlg.source] || "💬"} {detailDlg.source}{detailDlg.branch_name ? ` · 📍 ${detailDlg.branch_name}` : ""}</span>
                {!isMaster && (
                  <div className="flex items-center gap-1">
                    {detailDlg.phone && (
                      <button data-testid="cal-detail-remind-call" onClick={() => remindByCall(detailDlg.id)}
                        title={t("cal.remindCall")}
                        className="p-1.5 rounded text-gray-400 hover:text-black hover:bg-gray-100 transition-colors">
                        <PhoneCall className="w-4 h-4" />
                      </button>
                    )}
                    <button data-testid="cal-detail-cancel" onClick={() => cancelBooking(detailDlg.id)}
                      title={t("cal.cancelBooking")}
                      className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Mini month calendar (sidebar) ─────────────────────────────────────────
function PushButton({ t }) {
  const [state, setState] = useState("hidden"); // hidden | off | on
  useEffect(() => {
    if (!pushSupported()) return;
    isPushEnabled().then((on) => setState(on ? "on" : "off"));
  }, []);
  if (state !== "off") return null;
  return (
    <div className="px-2 pb-1">
      <button data-testid="cal-enable-push-btn"
        onClick={async () => {
          try {
            await enablePush();
            setState("on");
            toast.success(t("push.enabled") !== "push.enabled" ? t("push.enabled") : "Уведомления включены 🔔");
          } catch (e) {
            toast.error(t("push.denied") !== "push.denied" ? t("push.denied") : "Разрешите уведомления в браузере");
          }
        }}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors">
        🔔 {t("push.enable") !== "push.enable" ? t("push.enable") : "Включить уведомления"}
      </button>
    </div>
  );
}

function MiniCal({ anchor, onPick, locale }) {
  const [month, setMonth] = useState(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  useEffect(() => { setMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1)); }, [anchor]);
  const firstOffset = (month.getDay() + 6) % 7;
  const daysIn = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  // Jan 1 2024 is a Monday
  const dayNames = Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, i + 1).toLocaleDateString(locale, { weekday: "narrow" }));
  const todayStr = fmtDate(new Date());
  const selStr = fmtDate(anchor);
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  return (
    <div className="border border-gray-200 rounded-xl bg-white" data-testid="cal-minical">
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium text-sm capitalize pl-1">
          {month.toLocaleDateString(locale, { month: "long", year: "numeric" })}
        </span>
        <div className="flex gap-0.5">
          <button data-testid="minical-prev" onClick={() => shift(-1)} className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="minical-next" onClick={() => shift(1)} className="p-1 rounded hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-[10px] font-bold text-gray-400 mb-0.5">
        {dayNames.map((d, i) => <div key={i} className="uppercase">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 text-center">
        {Array.from({ length: firstOffset }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysIn }, (_, i) => {
          const d = new Date(month.getFullYear(), month.getMonth(), i + 1);
          const ds = fmtDate(d);
          const cls = ds === selStr ? "bg-gray-900 text-white font-medium"
            : ds === todayStr ? "bg-gray-100 font-semibold"
              : "hover:bg-gray-100";
          return (
            <button key={i} data-testid={`minical-day-${ds}`} onClick={() => onPick(d)}
              className={`w-7 h-7 mx-auto my-0.5 rounded-full text-xs flex items-center justify-center transition-colors ${cls}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Custom Day view: master columns + drag&drop ──────────────────────────
function DayGrid({ bookings, busy = [], staff, date, onMove, onCellClick, onCardClick, allStaff, readOnly, hourStart, hourEnd, branches }) {
  const [dragId, setDragId] = useState(null);
  const hs = hourStart ?? 8, he = hourEnd ?? 21;
  const rows = (he - hs) * 4;
  const colStaff = staff.length ? staff : [{ id: "_none", name: "—", color: "#94a3b8" }];

  const cardsFor = (staffId) => bookings.filter((b) => (b.staff_id || "_none") === staffId);
  const busyFor = (staffId) => busy.filter((e) => e.staff_id === staffId);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto" data-testid="cal-day-grid">
      <div className="min-w-[520px]" style={{ display: "grid", gridTemplateColumns: `56px repeat(${colStaff.length}, minmax(150px, 1fr))` }}>
        <div className="border-b border-gray-200" />
        {colStaff.map((s) => (
          <div key={s.id} className="border-b border-l border-gray-200 px-2 py-2 font-medium text-sm flex items-center gap-2 sticky top-0 bg-white" data-testid={`cal-col-${s.id}`}>
            <StaffAvatar s={s} size={22} />
            {s.name}
          </div>
        ))}
        {/* time column */}
        <div className="relative" style={{ height: rows * SLOT_PX }}>
          {Array.from({ length: he - hs }, (_, i) => (
            <div key={i} className="absolute right-1 text-[10px] text-gray-400 font-semibold" style={{ top: i * 4 * SLOT_PX - 6 }}>
              {i === 0 ? "" : m2t((hs + i) * 60)}
            </div>
          ))}
        </div>
        {colStaff.map((s) => (
          <div key={s.id} className="relative border-l border-gray-200" style={{ height: rows * SLOT_PX }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!dragId) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const mins = Math.round((e.clientY - rect.top) / SLOT_PX) * 15 + hs * 60;
              onMove(dragId, `${date}T${m2t(Math.max(hs * 60, Math.min(mins, he * 60 - 15)))}`, s.id === "_none" ? null : s.id);
              setDragId(null);
            }}>
            {Array.from({ length: rows }, (_, i) => (
              <div key={i} onClick={() => onCellClick(s.id === "_none" ? null : s.id, m2t(hs * 60 + i * 15))}
                className={`absolute left-0 right-0 cursor-pointer hover:bg-yellow-50 ${i % 4 === 0 ? "border-t border-gray-200" : ""}`}
                style={{ top: i * SLOT_PX, height: SLOT_PX }} />
            ))}
            {(() => {
              const w = s.id === "_none" ? null : dayWin(s, date, branches);
              if (!w) return null;
              const label = w.note || null;
              if (!w.enabled) return (
                <div className="absolute inset-0 pointer-events-none flex items-start justify-center pt-6 text-xs font-semibold text-gray-500"
                  style={{ background: "repeating-linear-gradient(135deg, #f3f4f6, #f3f4f6 8px, #e5e7eb 8px, #e5e7eb 16px)", opacity: 0.9 }}
                  data-testid={`cal-dayoff-${s.id}`}>🏖 {label || "—"}</div>
              );
              if (!w.override) return null;
              const a = Math.max(0, ((t2m(w.start) - hs * 60) / 15) * SLOT_PX);
              const b = Math.max(0, ((he * 60 - t2m(w.end)) / 15) * SLOT_PX);
              const shade = "repeating-linear-gradient(135deg, #f3f4f6, #f3f4f6 8px, #e5e7eb 8px, #e5e7eb 16px)";
              return (
                <>
                  {a > 0 && <div className="absolute left-0 right-0 top-0 pointer-events-none" style={{ height: a, background: shade, opacity: 0.9 }} data-testid={`cal-custom-before-${s.id}`} />}
                  {b > 0 && <div className="absolute left-0 right-0 bottom-0 pointer-events-none flex items-end justify-center pb-2 text-[10px] font-semibold text-gray-500" style={{ height: b, background: shade, opacity: 0.9 }} data-testid={`cal-custom-after-${s.id}`}>{label ? `🏖 ${label}` : ""}</div>}
                </>
              );
            })()}
            {busyFor(s.id).map((e, idx) => {
              const start = t2m(e.start_at.slice(11, 16));
              const end = e.end_at ? t2m(e.end_at.slice(11, 16)) : start + 60;
              const top = ((start - hs * 60) / 15) * SLOT_PX;
              const h = Math.max(((end - start) / 15) * SLOT_PX, SLOT_PX);
              const bc = s.color || "#9ca3af";
              return (
                <div key={`busy-${idx}`} data-testid={`cal-busy-${s.id}-${idx}`} title={e.summary || "Planity"}
                  className="absolute left-0.5 right-1 rounded-md px-1.5 py-0.5 text-[10px] leading-tight overflow-hidden font-semibold pointer-events-none"
                  style={{ top, height: h, color: bc, background: `repeating-linear-gradient(135deg, color-mix(in srgb, ${bc} 22%, #fff), color-mix(in srgb, ${bc} 22%, #fff) 6px, color-mix(in srgb, ${bc} 9%, #fff) 6px, color-mix(in srgb, ${bc} 9%, #fff) 12px)`, borderLeft: `3px solid ${bc}` }}>
                  <div>🔒 {e.start_at.slice(11, 16)}</div>
                  <div className="truncate opacity-80">Planity</div>
                </div>
              );
            })}
            {cardsFor(s.id).map((b) => {
              const start = t2m(b.start_at.slice(11, 16));
              const end = b.end_at ? t2m(b.end_at.slice(11, 16)) : start + 60;
              const top = ((start - hs * 60) / 15) * SLOT_PX;
              const h = Math.max(((end - start) / 15) * SLOT_PX, SLOT_PX * 2);
              const color = (allStaff.find((x) => x.id === b.staff_id) || {}).color || "#0ea5e9";
              return (
                <div key={b.id} draggable={!readOnly} onDragStart={() => !readOnly && setDragId(b.id)} onDragEnd={() => setDragId(null)}
                  onClick={(e) => { e.stopPropagation(); onCardClick(b); }}
                  data-testid={`cal-card-${b.id}`}
                  className="absolute left-0.5 right-1 rounded-md border border-black/10 px-1.5 py-0.5 text-[11px] leading-tight cursor-grab active:cursor-grabbing overflow-hidden text-white font-medium shadow-sm"
                  style={{ top, height: h, background: color, opacity: b.attendance === "no_show" ? 0.55 : 1,
                    border: b.admin_confirmed === false ? "2px dashed #fbbf24" : undefined }}>
                  {(() => {
                    const lines = Math.floor((h - 4) / 13); // 11px text ≈ 13px line
                    const head = <>{b.start_at.slice(11, 16)} {b.admin_confirmed === false ? "⏳" : ""}{b.attendance === "came" ? "✅" : b.attendance === "no_show" ? "❌" : SRC_ICON[b.source] || ""}
                      {b.client_confirmed_at && !b.attendance && <span data-testid={`cal-card-confirmed-${b.id}`} title="✓ confirmed by client" className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-green-500 text-white text-[9px] font-bold align-middle">✓</span>}</>;
                    return (
                      <div title={`${b.start_at.slice(11, 16)}${b.end_at ? "–" + b.end_at.slice(11, 16) : ""} · ${b.service || ""} · ${b.client_name || ""}`}>
                        {lines >= 3 ? (
                          <>
                            <div>{head}</div>
                            <div className="truncate">{b.client_name}</div>
                            <div className="truncate opacity-90 font-normal" data-testid={`cal-card-service-${b.id}`}>{b.service}</div>
                          </>
                        ) : lines === 2 ? (
                          <>
                            <div className="flex gap-1 min-w-0"><span className="shrink-0">{head}</span><span className="truncate opacity-90 font-normal" data-testid={`cal-card-service-${b.id}`}>· {b.service}</span></div>
                            <div className="truncate">{b.client_name}</div>
                          </>
                        ) : (
                          <div className="flex gap-1 min-w-0"><span className="shrink-0">{head}</span><span className="truncate opacity-90 font-normal" data-testid={`cal-card-service-${b.id}`}>· {b.service}</span><span className="truncate">· {b.client_name}</span></div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
